/**
 * The Team card at the top of the settings sheet: everything that is not an
 * Agent. How far it goes on its own (the Team ladder), Name, Owns, Works on, System, Reads (an echo of what Bailey reads, edited
 * under Bailey), Whose tickets, Posts to, People, Asks, Seen by, the Playbook, and the
 * two acts that land at once, Pause team and Archive.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { RepoMoreMatches, RepoScopeLine, RepoSelectAll } from "../../desks/RepoScopeLine.js";
import { Btn } from "../../queue/index.js";
import { SUBSCRIPTIONS_CHANGED_EVENT } from "../../shell/useNavDesks.js";
import {
  BACKLOG_SOURCE_WORD,
  EDIT_ARCHIVE_CONFIRM,
  EDIT_OWNS_PLACEHOLDER,
  EDIT_POSTS_NOWHERE,
  EDIT_WHOSE_ANYONE,
  EDIT_WHOSE_CHOSEN,
  EDIT_WHOSE_PERSON,
  EDIT_WHOSE_UNASSIGNED,
  QUESTION_KINDS,
  CONNECTIONS_LABEL,
} from "../copy.js";
import { pauseTeam, resumeTeam } from "../teamsApi.js";
import { ReadsPanel } from "../ReadsPanel.js";
import type { SettingsCtx } from "./context.js";
import { AgentCard, AreaField, Field, Seg, Sect, SelectField, TextField } from "./fields.js";
import { LadderField } from "./LadderField.js";
import { PlaybookRules } from "./PlaybookRules.js";
import { PeopleField } from "./PeopleField.js";
import { TeamRepoAgentTrust } from "../TeamRepoAgentTrust.js";
import { attachedRepos } from "../useTeamPage.js";
import { ASKS_HELP, SEEN_BY_HELP } from "../people/people.js";
import {
  WHOSE_ANYONE,
  WHOSE_CHOSEN,
  WHOSE_UNASSIGNED,
  readsAttachment,
  whoseValue,
} from "./settingsDraft.js";

export function TeamSection({ ctx }: { ctx: SettingsCtx }) {
  const navigate = useNavigate();
  const { team, page } = ctx;
  const disabled = ctx.readOnly || ctx.busy;
  const [repoQuery, setRepoQuery] = useState("");
  const [searching, setSearching] = useState(false);

  // Every repository not yet picked that the search matches; ten are listed,
  // and Select all picks every one of them.
  const allRepoMatches = useMemo(() => {
    const query = repoQuery.trim().toLowerCase();
    return ctx.availableRepos.filter(
      (repo) => !team.repos.includes(repo) && (!query || repo.toLowerCase().includes(query)),
    );
  }, [ctx.availableRepos, team.repos, repoQuery]);
  const repoMatches = allRepoMatches.slice(0, 10);

  // None, then every system; the Team's own pick stays listed when the
  // viewer cannot read the list.
  const systemOptions = useMemo(() => {
    const listed = ctx.systems.map((system) => ({ value: system.id, label: system.name }));
    const picked = page.team?.declaredSystemId;
    if (picked && !listed.some((option) => option.value === picked))
      listed.push({ value: picked, label: page.team?.declaredSystemName ?? "This Team's system" });
    return [{ value: "", label: "None" }, ...listed];
  }, [ctx.systems, page.team?.declaredSystemId, page.team?.declaredSystemName]);

  const scopeLine = (
    <RepoScopeLine
      scope={ctx.repoScope ?? null}
      listed={ctx.availableRepos.length}
      testId="settings-team-repo-scope"
    />
  );

  const att = readsAttachment(page.attachments);
  const sourceWord = att ? (BACKLOG_SOURCE_WORD[att.source] ?? att.source) : null;
  const readsEcho = team.filter.trim()
    ? `${sourceWord ?? (team.tracker === "linear" ? "Linear" : "Jira")} · ${team.filter.trim()}`
    : att
      ? `${sourceWord} · ${att.label || att.ref}`
      : "Nothing yet. Bailey reads only what you hand it.";

  const nameOf = (userId: string) =>
    ctx.people.find((person) => person.userId === userId)?.name ?? userId;
  // Asks pick only from the Team's People: an answerer has to be on the Team,
  // and adding someone is People's job, never a side effect of asking them.
  const askable = team.people
    .filter((userId) => !team.asks.some((row) => row.userId === userId))
    .map((userId) => ({ userId, name: nameOf(userId) }));
  const initialWhose = whoseValue(page.ticketsAssignedTo);

  const channelOptions = useMemo(() => {
    const out = ctx.slackChannels.map((entry) => ({ value: entry.id, label: `#${entry.name}` }));
    const current = page.channels[0];
    if (current && !out.some((entry) => entry.value === current.channelId)) {
      out.unshift({
        value: current.channelId,
        label: current.label.startsWith("#") ? current.label : `#${current.label}`,
      });
    }
    return [...out, { value: "", label: EDIT_POSTS_NOWHERE }];
  }, [ctx.slackChannels, page.channels]);

  const archive = async () => {
    if (!window.confirm(EDIT_ARCHIVE_CONFIRM)) return;
    const ok = await ctx.act(async () => {
      const res = await fetch(`/api/desks/${encodeURIComponent(ctx.deskId)}/archive`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Could not archive the Team (${res.status}).`);
    });
    if (ok) {
      window.dispatchEvent(new Event(SUBSCRIPTIONS_CHANGED_EVENT));
      navigate("/teams", { replace: true });
    }
  };

  return (
    <AgentCard
      id="team"
      mark={<span className="tdot" aria-hidden />}
      name={team.name || page.team?.name || "This Team"}
      role="Everything that is not an agent"
    >
      <LadderField ctx={ctx} />
      <TextField
        label="Name"
        value={team.name}
        maxLength={120}
        disabled={disabled}
        testId="settings-team-name"
        onChange={(name) => ctx.setTeam((t) => ({ ...t, name }))}
      />
      <AreaField
        label="Owns"
        rows={2}
        value={team.owns}
        placeholder={EDIT_OWNS_PLACEHOLDER}
        disabled={disabled}
        testId="settings-team-owns"
        onChange={(owns) => ctx.setTeam((t) => ({ ...t, owns }))}
      />
      <Field label="Works on" top testId="settings-team-repos">
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          <div className="tags">
            {team.repos.map((repo) => (
              <span key={repo} className="chip entry">
                {repo}
                {!disabled && (
                  <button
                    type="button"
                    className="x"
                    aria-label={`Remove ${repo}`}
                    onClick={() =>
                      ctx.setTeam((t) => ({
                        ...t,
                        repos: t.repos.filter((entry) => entry !== repo),
                      }))
                    }
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {!disabled && !searching && (
              <button
                type="button"
                className="chip add"
                onClick={() => setSearching(true)}
                data-testid="settings-team-repo-search-open"
              >
                + search {ctx.availableRepos.length}{" "}
                {ctx.availableRepos.length === 1 ? "repository" : "repositories"}
              </button>
            )}
          </div>
          <TeamRepoAgentTrust
            repos={team.repos.filter((repo) => attachedRepos(page.attachments).includes(repo))}
            disabled={disabled}
          />
          {!searching && scopeLine}
          {searching && (
            <>
              <div className="in">
                <input
                  autoFocus
                  type="search"
                  value={repoQuery}
                  placeholder="Search repositories"
                  aria-label="Search repositories"
                  data-testid="settings-team-repo-search"
                  onChange={(event) => setRepoQuery(event.target.value)}
                />
              </div>
              {scopeLine}
              <div className="checklist" data-testid="settings-team-repo-matches">
                <RepoSelectAll
                  count={allRepoMatches.length}
                  testId="settings-team-repo-select-all"
                  onSelect={() => {
                    ctx.setTeam((t) => ({
                      ...t,
                      repos: [
                        ...t.repos,
                        ...allRepoMatches.filter((repo) => !t.repos.includes(repo)),
                      ],
                    }));
                    setRepoQuery("");
                  }}
                />
                {repoMatches.map((repo) => (
                  <label key={repo}>
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => {
                        ctx.setTeam((t) => ({ ...t, repos: [...t.repos, repo] }));
                        setRepoQuery("");
                      }}
                    />
                    <span>{repo}</span>
                  </label>
                ))}
                {repoMatches.length === 0 && (
                  <span className="fact" style={{ padding: "2px 8px" }}>
                    No repository matches.
                  </span>
                )}
                <RepoMoreMatches hidden={allRepoMatches.length - repoMatches.length} />
              </div>
            </>
          )}
        </div>
      </Field>
      <SelectField
        label="System"
        value={team.declaredSystemId ?? ""}
        disabled={disabled}
        testId="settings-team-system"
        help="The repositories a build on this Team starts with. Systems are set in Settings."
        options={systemOptions}
        onChange={(id) => ctx.setTeam((t) => ({ ...t, declaredSystemId: id || null }))}
      />
      <Field label={CONNECTIONS_LABEL} top testId="settings-team-connections">
        <div id="set-connections" style={{ minWidth: 0 }}>
          <ReadsPanel
            deskId={ctx.deskId}
            disabled={disabled}
            onChanged={() => void ctx.page.refetch()}
          />
        </div>
      </Field>
      <Field label="Reads">
        <div className="in mono" style={{ gap: 8 }}>
          <span
            style={{ overflow: "hidden", textOverflow: "ellipsis" }}
            title={team.filter.trim() || undefined}
            data-testid="settings-team-reads"
          >
            {readsEcho}
          </span>
          <button
            type="button"
            className="ref quiet"
            style={{ marginLeft: "auto", flex: "none" }}
            onClick={() => ctx.goto("bailey")}
          >
            {ctx.matches != null ? `${ctx.matches} match · ` : ""}edit under Bailey
          </button>
        </div>
      </Field>
      <SelectField
        label="Whose tickets"
        value={team.whose}
        disabled={disabled}
        testId="settings-team-whose"
        onChange={(whose) => ctx.setTeam((t) => ({ ...t, whose }))}
        options={[
          { value: WHOSE_ANYONE, label: EDIT_WHOSE_ANYONE },
          { value: WHOSE_UNASSIGNED, label: EDIT_WHOSE_UNASSIGNED },
          ...(initialWhose === WHOSE_CHOSEN
            ? [{ value: WHOSE_CHOSEN, label: EDIT_WHOSE_CHOSEN }]
            : []),
          ...ctx.people.map((person) => ({
            value: person.userId,
            label: EDIT_WHOSE_PERSON(person.name),
          })),
        ]}
      />
      <SelectField
        label="Posts to"
        value={team.channel}
        disabled={disabled}
        testId="settings-team-posts"
        options={channelOptions}
        onChange={(channel) => ctx.setTeam((t) => ({ ...t, channel }))}
      />
      <div id="set-people">
        <PeopleField ctx={ctx} disabled={disabled} />
      </div>
      <Field label="Asks" top help={ASKS_HELP} testId="settings-team-asks">
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          {team.asks.map((row, index) => (
            <div className="inline" key={`${row.userId}-${index}`}>
              <span className="in" style={{ flex: 1 }}>
                {nameOf(row.userId)} answers about
              </span>
              <select
                aria-label={`${nameOf(row.userId)} answers about`}
                value={row.kind}
                disabled={disabled}
                onChange={(event) =>
                  ctx.setTeam((t) => ({
                    ...t,
                    asks: t.asks.map((entry, at) =>
                      at === index ? { ...entry, kind: event.target.value } : entry,
                    ),
                  }))
                }
              >
                {QUESTION_KINDS.map((kind) => (
                  <option key={kind.key} value={kind.key}>
                    {kind.about}
                  </option>
                ))}
              </select>
              {!disabled && (
                <Btn
                  variant="quiet"
                  size="sm"
                  onClick={() =>
                    ctx.setTeam((t) => ({ ...t, asks: t.asks.filter((_, at) => at !== index) }))
                  }
                >
                  Stop asking
                </Btn>
              )}
            </div>
          ))}
          {!disabled && askable.length > 0 && (
            <select
              aria-label="Add a person to ask"
              value=""
              style={{ maxWidth: 220 }}
              data-testid="settings-team-ask-add"
              onChange={(event) => {
                const userId = event.target.value;
                if (userId)
                  ctx.setTeam((t) => ({ ...t, asks: [...t.asks, { userId, kind: "product" }] }));
              }}
            >
              <option value="">Add a person</option>
              {askable.map((person) => (
                <option key={person.userId} value={person.userId}>
                  {person.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </Field>
      <Field label="Seen by" help={SEEN_BY_HELP}>
        <Seg
          label="Seen by"
          value={team.audience}
          disabled={disabled}
          testId="settings-team-seen"
          onChange={(audience) => ctx.setTeam((t) => ({ ...t, audience }))}
          options={[
            { value: "restricted", label: "Members only" },
            { value: "shared", label: "Everyone in the workspace" },
          ]}
        />
      </Field>
      <Sect>Playbook · standing rules read on every run</Sect>
      <PlaybookRules
        deskId={ctx.deskId}
        rules={page.playbookRules ?? []}
        canEdit={!ctx.readOnly}
        onChanged={page.refetch}
      />
      {!ctx.readOnly && (
        <div className="acts">
          <Btn
            variant="quiet"
            size="sm"
            disabled={ctx.busy}
            data-testid="settings-team-pause"
            onClick={() =>
              void ctx.act(
                () => (page.paused.paused ? resumeTeam(ctx.deskId) : pauseTeam(ctx.deskId)),
                page.paused.paused
                  ? "The Team is running again."
                  : "The Team is paused. Nothing new starts until it resumes.",
              )
            }
          >
            {page.paused.paused ? "Resume team" : "Pause team"}
          </Btn>
          <Btn
            variant="quiet"
            size="sm"
            style={{ color: "var(--incident)" }}
            disabled={ctx.busy}
            data-testid="settings-team-archive"
            onClick={() => void archive()}
          >
            Archive
          </Btn>
        </div>
      )}
    </AgentCard>
  );
}
